// @ts-strict-ignore
import * as React from 'react';
import {clsx} from 'clsx';

import {useTranslation} from 'common/i18n/hooks';
import {InputReact} from 'common/components/input';
import {parseNumber} from 'common/util';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {InputChangeEvent, InputSize} from 'common/components/input/types';

import {FilterValueProps} from '../types';

const MULTIPLE_VALUES_OPERATORS = new Set<NumberOperator>([NumberOperator.Between, NumberOperator.NotBetween]);

export function FilterNumericValue(props: FilterValueProps) {
  // set and not set do not require a filter value input
  if (
    (props.filter.filterOperator as NumberOperator) === NumberOperator.Set ||
    (props.filter.filterOperator as NumberOperator) === NumberOperator.NotSet
  ) {
    return null;
  }

  const {filterOperator, filterValue} = props.filter;
  const {t} = useTranslation();

  const inputEl = (
    currentValue: number,
    handleInput: (number) => void,
    secondIndex?: number,
    centerInput?: boolean,
  ) => {
    const isInvalid = parseNumber(currentValue) === null;
    const shouldAutoFocus = props.isActive && !secondIndex;
    const value = currentValue?.toString(10) ?? ``;

    return (
      <InputReact
        className={clsx(`filter-input`, {
          'is-placeholder': isInvalid,
          'center-input': centerInput,
        })}
        elref='filterInput'
        size={props.hasNewUI ? InputSize.Small : InputSize.Medium}
        type='number'
        placeholder={t(`query-builder.numeric-value-placeholder`)}
        autoFocus={!!shouldAutoFocus}
        selectOnFocus
        readonly={props.readonly}
        value={value}
        onChange={(ev: InputChangeEvent) => {
          const input = ev.target as HTMLInputElement;

          // when the updated value is not a valid input, revert back to previously committed input
          const updatedValue = parseNumber(ev.detail.value);
          if (updatedValue !== null) {
            handleInput(updatedValue);
          } else {
            input.value = value;
          }

          input.blur(); // user has most likely pressed "Enter", in which case they are likely finished so we want to lose focus
        }}
      />
    );
  };

  // handle cases where the numeric filter requires 2 values
  if (MULTIPLE_VALUES_OPERATORS.has(filterOperator as NumberOperator)) {
    const [minValue, maxValue] = filterValue as number[];
    return (
      <>
        {inputEl(minValue, (value) => props.onChange([value, maxValue]), 0, props.hasNewUI)}
        <div className='entry-filter-conjunction'>and</div>
        {inputEl(maxValue, (value) => props.onChange([minValue, value]), 1, props.hasNewUI)}
      </>
    );
  }

  return <>{inputEl(filterValue as number, (value) => props.onChange(value))}</>;
}
